import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import BaseLayout from './layouts/BaseLayout';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import EmailVerifyPage from './pages/EmailVerifyPage';
import VerifiedPage from './pages/VerifiedPage';
import SocialLoginCallbackPage from './pages/SocialLoginCallbackPage';
import ProtectedRoute from './components/ProtectedRoute';
import UnprotectedRoute from './components/UnprotectedRoute';
import CardDetailPage from './pages/CardDetailPage';
import PurchasePage from './pages/PurchasePage';
import CreateListingPage from './pages/CreateListingPage';
import CardListingPage from './pages/CardListingPage';
import ListingDetailPage from './pages/ListingDetailPage';
import { useUserProfile } from './hooks/useUserProfile';
import { useMetadata } from './hooks/useMetadata';
import MePage from './pages/MePage';
import ProfileInfoPage from './pages/me/ProfileInfoPage';
import FavoritesPage from './pages/me/FavoritesPage';
import OrdersPage from './pages/me/OrdersPage';
import WalletPage from './pages/me/WalletPage';
import SalesPage from './pages/me/SalesPage';
import ListingsPage from './pages/me/ListingsPage';
import AddressPage from './pages/me/AddressPage';
import PaymentPage from './pages/me/PaymentPage';

import NotificationsPage from './pages/me/NotificationsPage';
import IdentityVerificationPage from './pages/me/IdentityVerificationPage';
import BrandPage from './pages/BrandPage';
import SearchPage from './pages/SearchPage';
import PaymentDonePage from './pages/PaymentDonePage';
import ContactPage from './pages/ContactPage';
import IntroductionPcPage from './pages/IntroductionPcPage';
import IntroductionIosPage from './pages/IntroductionIosPage';
import IntroductionBenefitFeePcPage from './pages/IntroductionBenefitFeePcPage';
import IntroductionBenefitFeeIosPage from './pages/IntroductionBenefitFeeIosPage';
import QualityGuidelinePSAPage from './pages/terms/QualityGuidelinePSAPage';
import QualityGuidelineNonPSAPage from './pages/terms/QualityGuidelineNonPSAPage';
import QualityGuidelineBoxPage from './pages/terms/QualityGuidelineBoxPage';
import QualityGuidelineUnopenedCardPage from './pages/terms/QualityGuidelineUnopenedCardPage';
import QualityGuidelineUnopenedPackPage from './pages/terms/QualityGuidelineUnopenedPackPage';
import QualityGuidelineFigureHobbyPage from './pages/terms/QualityGuidelineFigureHobbyPage';
import TermsOfUsePage from './pages/terms/TermsOfUsePage';
import GuidelineListingPage from './pages/terms/GuidelineListingPage';
import PrivacyPolicyPage from './pages/terms/PrivacyPolicyPage';
import QualityGuidelinePhotoPage from './pages/terms/QualityGuidelinePhotoPage';
import GuidelineTransactionPage from './pages/terms/GuidelineTransactionPage';
import GuidelinePackingShippingPage from './pages/terms/GuidelinePackingShippingPage';
import GuidelinePackSingleCardPage from './pages/terms/GuidelinePackSingleCardPage';
import SaleTransferPage from './pages/terms/SaleTransferPage';
import RequestWithdrawalPage from './pages/terms/RequestWithdrawalPage';
import CompensationPage from './pages/terms/CompensationPage';
import PaymentMethodsPage from './pages/terms/PaymentMethodsPage';
import FeesChargesPage from './pages/terms/FeesChargesPage';
import SpamBehaviorPage from './pages/terms/SpamBehaviorPage';
import RestrictionsPage from './pages/terms/RestrictionsPage';
import FaqPackingShippingPage from './pages/terms/FaqPackingShippingPage';
import FaqTradePurchaseSellPage from './pages/terms/FaqTradePurchaseSellPage';
import FaqTransferPaymentPage from './pages/terms/FaqTransferPaymentPage';
import FaqCompensationReturnReassessmentPage from './pages/terms/FaqCompensationReturnReassessmentPage';
import FaqMembershipLoginPage from './pages/terms/FaqMembershipLoginPage';
import FaqTroubleshootingRestrictionsPage from './pages/terms/FaqTroubleshootingRestrictionsPage';
import CommercialTransactionInfoPage from './pages/terms/CommercialTransactionInfoPage';
import ExternalTransmissionPolicyPage from './pages/terms/ExternalTransmissionPolicyPage';
import { ToastProvider } from './components/ToastProvider';
import NavigationHandler from './components/NavigationHandler';
import { initializeRecaptcha } from './utils/recaptcha';
import useRecaptchaBadgeManager from './hooks/useRecaptchaBadgeManager';
import { initializeGA, configureUserGA } from './utils/googleAnalytics';
import useGoogleAnalytics from './hooks/useGoogleAnalytics';
import useAuthStore from './stores/authStore';

// Component that handles router-dependent hooks
function AppContent() {
  // This will automatically fetch the user profile when there's a token
  useUserProfile();

  // Prefetch metadata on app start - cached for 1 hour
  // This ensures metadata (prefectures, inquiryTypes) is available when needed
  useMetadata();

  // Manage reCAPTCHA badge visibility based on auth status
  useRecaptchaBadgeManager();

  // Track page views with Google Analytics (needs to be inside Router)
  useGoogleAnalytics();

  return (
    <>
      <NavigationHandler />
      <Routes>
        <Route path="/payment/done" element={<PaymentDonePage />} />
        <Route path="/introduction/pc" element={<IntroductionPcPage />} />
        <Route path="/introduction/ios" element={<IntroductionIosPage />} />
        <Route path="/introduction/benefit_fee/pc" element={<IntroductionBenefitFeePcPage />} />
        <Route path="/introduction/benefit_fee/ios" element={<IntroductionBenefitFeeIosPage />} />
        <Route path="/terms/quality_guideline_psa" element={<QualityGuidelinePSAPage />} />
        <Route path="/terms/quality_guideline_non_psa" element={<QualityGuidelineNonPSAPage />} />
        <Route path="/terms/quality_guideline_box" element={<QualityGuidelineBoxPage />} />
        <Route path="/terms/quality_guideline_unopened_card" element={<QualityGuidelineUnopenedCardPage />} />
        <Route path="/terms/quality_guideline_unopened_pack" element={<QualityGuidelineUnopenedPackPage />} />
        <Route path="/terms/quality_guideline_figure_hobby" element={<QualityGuidelineFigureHobbyPage />} />
        <Route path="/terms/terms_of_use" element={<TermsOfUsePage />} />
        <Route path="/terms/guideline_listing" element={<GuidelineListingPage />} />
        <Route path="/terms/privacy_policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms/guideline_photo" element={<QualityGuidelinePhotoPage />} />
        <Route path="/terms/guideline_transaction" element={<GuidelineTransactionPage />} />
        <Route path="/terms/guideline_packing_shipping" element={<GuidelinePackingShippingPage />} />
        <Route path="/terms/guideline_pack_single_card" element={<GuidelinePackSingleCardPage />} />
        <Route path="/terms/sale_transfer" element={<SaleTransferPage />} />
        <Route path="/terms/request_withdrawal" element={<RequestWithdrawalPage />} />
        <Route path="/terms/compensation" element={<CompensationPage />} />
        <Route path="/terms/payment_methods" element={<PaymentMethodsPage />} />
        <Route path="/terms/fees_charges" element={<FeesChargesPage />} />
        <Route path="/terms/spam_behavior" element={<SpamBehaviorPage />} />
        <Route path="/terms/restrictions" element={<RestrictionsPage />} />
        <Route path="/terms/faq_packing_shipping" element={<FaqPackingShippingPage />} />
        <Route path="/terms/faq_trade_purchase_sell" element={<FaqTradePurchaseSellPage />} />
        <Route path="/terms/faq_transfer_payment" element={<FaqTransferPaymentPage />} />
        <Route path="/terms/faq_compensation_return_reassessment" element={<FaqCompensationReturnReassessmentPage />} />
        <Route path="/terms/faq_membership_login" element={<FaqMembershipLoginPage />} />
        <Route path="/terms/faq_troubleshooting_restrictions" element={<FaqTroubleshootingRestrictionsPage />} />
        <Route path="/terms/commercial_transaction_info" element={<CommercialTransactionInfoPage />} />
        <Route path="/terms/external_transmission_policy" element={<ExternalTransmissionPolicyPage />} />

        <Route path="/" element={<BaseLayout />}>
          <Route index element={<HomePage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="login" element={<UnprotectedRoute><LoginPage /></UnprotectedRoute>} />
          <Route path="signup" element={<UnprotectedRoute><SignupPage /></UnprotectedRoute>} />
          <Route path="forgot-password" element={<UnprotectedRoute><ForgotPasswordPage /></UnprotectedRoute>} />
          <Route path="verified" element={<ProtectedRoute><VerifiedPage /></ProtectedRoute>} />
          <Route path="email-verify" element={<EmailVerifyPage />} />
          <Route path="/auth/:provider/callback" element={<SocialLoginCallbackPage />} />
          <Route path="card/:id" element={<CardDetailPage />} />
          <Route path="listing/:id" element={<ListingDetailPage />} />
          <Route path="brands/:id" element={<BrandPage />} />
          <Route path="card/:id/purchase" element={<ProtectedRoute><PurchasePage /></ProtectedRoute>} />
          <Route path="card/:id/sell" element={<ProtectedRoute><CreateListingPage /></ProtectedRoute>} />
          <Route path="card/:id/listing" element={<CardListingPage />} />
          <Route path="listing/edit/:listingId" element={<ProtectedRoute><CreateListingPage /></ProtectedRoute>} />

          <Route path="me" element={<ProtectedRoute><MePage /></ProtectedRoute>}>
            <Route index element={<ProfileInfoPage />} />
            <Route path="favorites" element={<FavoritesPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="wallet" element={<WalletPage />} />
            <Route path="listings" element={<ListingsPage />} />
            <Route path="sales" element={<SalesPage />} />
            <Route path="address" element={<AddressPage />} />
            <Route path="payment" element={<PaymentPage />} />

          </Route>
          <Route path="me/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
          <Route path="venders/protechidchecker" element={<ProtectedRoute><IdentityVerificationPage /></ProtectedRoute>} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  const user = useAuthStore((state) => state.user);

  // Initialize reCAPTCHA and Google Analytics on app start
  useEffect(() => {
    initializeRecaptcha();
    initializeGA();
  }, []);

  // Update GA configuration when user auth state changes
  useEffect(() => {
    configureUserGA(user);
  }, [user]);

  return (
    <ToastProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
